import React, { useContext, useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, DialogTitle, Typography, Chip, Snackbar, Alert, IconButton, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DateTime } from 'luxon';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';
import { PostData } from '../model/types';
import { formatTimeUntilOpen } from '../lib/timeCapsule';

const DONATE_PRESETS: Array<{ label: string; seconds: number }> = [
  { label: '5 min',  seconds: 300 },
  { label: '30 min', seconds: 1800 },
  { label: '1 hour', seconds: 3600 },
  { label: '6 hours', seconds: 21600 },
  { label: '1 day', seconds: 86400 },
  { label: '1 week', seconds: 604800 },
];

function CapsuleDonate({
  post,
  onDonated,
}: {
  post: PostData;
  onDonated?: (newOpenAtISO: string) => void;
}) {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [open, setOpen] = useState(false);
  const [donating, setDonating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (!userRecord) return null;
  if (!post.open_at) return null;
  const buried = DateTime.fromISO(post.open_at) > DateTime.now();
  if (!buried) return null;

  const handleDonate = async (seconds: number) => {
    if (!userRecord) return;
    if (seconds > userRecord.time_wallet_seconds) {
      setToast('Not enough time in your wallet');
      return;
    }
    setDonating(true);
    try {
      const { data, error } = await supabase.rpc('donate_to_capsule', {
        p_post_id: post.id,
        p_seconds: seconds,
      });
      if (error) throw error;
      const newOpenAt = data as string | null;
      setToast(
        newOpenAt && DateTime.fromISO(newOpenAt) <= DateTime.now()
          ? '🔓 CAPSULE OPENED! It just unlocked — refresh to see.'
          : `⏳ Donated — capsule now opens in ${newOpenAt ? formatTimeUntilOpen(newOpenAt) : 'less time'}`
      );
      if (newOpenAt && onDonated) onDonated(newOpenAt);
      setOpen(false);
    } catch (e: any) {
      console.error('Donate capsule failed:', e);
      setToast(e.message ?? 'Donation failed');
    } finally {
      setDonating(false);
    }
  };

  const donatedSoFar = post.capsule_donated_seconds ?? 0;
  const donatedLabel = (() => {
    if (donatedSoFar === 0) return null;
    const d = Math.floor(donatedSoFar / 86400);
    const h = Math.floor((donatedSoFar % 86400) / 3600);
    const m = Math.floor((donatedSoFar % 3600) / 60);
    if (d > 0) return `${d}d ${h}h donated so far`;
    if (h > 0) return `${h}h ${m}m donated so far`;
    return `${m}m donated so far`;
  })();

  return (
    <>
      <Stack spacing={0.5} alignItems="flex-start">
        {donatedLabel && (
          <Chip
            label={donatedLabel}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              bgcolor: 'transparent',
              color: '#b79dff',
              border: '1px solid',
            }}
          />
        )}
        <Button
          size="small"
          variant="contained"
          onClick={() => setOpen(true)}
          startIcon={<LockOpenIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            fontFamily: '"VT323", monospace',
            letterSpacing: 1,
            fontSize: '0.85rem',
            bgcolor: 'text.primary',
            color: 'white',
            '&:hover': { bgcolor: 'text.primary' },
          }}
        >
          DONATE TIME TO OPEN EARLY
        </Button>
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pr: 6 }}>
          <Box sx={{ fontFamily: '"VT323", monospace', letterSpacing: 2, fontSize: '1.15rem', color: 'text.primary' }}>
            CROWD UNLOCK A CAPSULE
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Your donation moves the open-date forward. Both the buried
            period and the post's expiry shift together, so the poster
            keeps their full lifespan.
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover', border: '1px dashed' }}>
            <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, display: 'block' }}>
              🔒 Currently opens in {formatTimeUntilOpen(post.open_at)}
            </Typography>
            {donatedLabel && (
              <Typography variant="caption" color="text.secondary">
                {donatedLabel}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
            Your wallet: {Math.floor((userRecord.time_wallet_seconds ?? 0) / 3600)}h available
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {DONATE_PRESETS.map((p) => {
              const canAfford = userRecord.time_wallet_seconds >= p.seconds;
              return (
                <Chip
                  key={p.label}
                  label={`-${p.label}`}
                  clickable={canAfford && !donating}
                  disabled={!canAfford || donating}
                  onClick={() => canAfford && !donating && handleDonate(p.seconds)}
                  sx={{
                    fontFamily: '"VT323", monospace',
                    letterSpacing: 0.5,
                    fontSize: '0.9rem',
                    bgcolor: canAfford ? 'action.hover' : 'action.disabledBackground',
                    color: canAfford ? 'text.primary' : 'text.disabled',
                    border: '1px solid',
                    borderColor: canAfford ? 'divider' : 'divider',
                    '&:hover': canAfford ? { bgcolor: 'action.selected' } : {},
                  }}
                />
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CapsuleDonate;
